// Server-side funnel-step ping.
//
// Companion to legacy-dispatch.ts's emitLegacyStep. Each time the widget
// crosses a funnel step, we also POST a minimal payload to the middleware
// so the bookings viewer can render a server-side, attribution-stable
// funnel report that doesn't rely on GA4. See middleware
// src/api/funnel.controller.ts for the storage rationale.
//
// Fire-and-forget by design: failures must never block the booking flow
// or surface in the TRAFFIC drawer. We deliberately skip apiFetch so
// these pings don't pollute the in-widget traffic instrumentation.

import { getAttribution } from './attribution';

interface PingExtras {
  step_name?: string;
  a_number?: string;
  location?: string;
  fitting_selection?: string;
  value?: number;
  currency?: string;
}

export function pingFunnelStep(
  submissionId: string,
  step: number,
  extras: PingExtras = {},
): void {
  if (typeof window === 'undefined') return;
  const cfg = window.CC_WIDGET_CONFIG ?? {};
  const apiBaseUrl = cfg.apiBaseUrl;
  const bearerToken = cfg.bearerToken;
  if (!apiBaseUrl || !bearerToken) return;

  const attr = getAttribution();
  const body: Record<string, unknown> = {
    submission_id: submissionId,
    step,
    step_name: extras.step_name,
    utm_source: attr.utm_source,
    utm_medium: attr.utm_medium,
    utm_campaign: attr.utm_campaign,
    utm_content: attr.utm_content,
    utm_term: attr.utm_term,
    gclid: attr.gclid,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    a_number: extras.a_number,
    location: extras.location,
    fitting_selection: extras.fitting_selection,
    value: extras.value,
    currency: extras.currency,
  };
  // Strip undefined so the middleware's strict additionalProperties
  // validation doesn't reject the request and so log lines stay clean.
  for (const k of Object.keys(body)) {
    if (body[k] === undefined) delete body[k];
  }

  const url = `${apiBaseUrl.replace(/\/$/, '')}/analytics/funnel-step`;
  try {
    void fetch(url, {
      method: 'POST',
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
      },
    });
  } catch {
    // Swallow — analytics never blocks the booking flow.
  }
}
